// 인증서 템플릿별 스타일 설정

export type TemplateType = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15 | 16 | 17 | 18 | 19 | 20 | 21 | 22;

interface PositionStyle {
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  transform?: string;
}

interface SizeStyle {
  width?: string;
  height?: string;
  maxHeight?: string;
}

interface TextStyle {
  fontFamily?: string;
  fontWeight?: number;
  fontSize?: string;
  fontStyle?: string;
  color?: string;
  letterSpacing?: string;
  textTransform?: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  textAlign?: 'left' | 'center' | 'right';
  marginBottom?: string;
}

interface ElementConfig {
  position: PositionStyle;
  size?: SizeStyle;
  text?: TextStyle;
}

interface TemplateConfig {
  backgroundImage: string;
  hasBorder: boolean;
  // 세로형(A4 세로) 여부 — 컨테이너 비율/최대폭 분기 (기본: 가로형)
  portrait?: boolean;
  // 강사(서명) 2명 + 직위 없음 형태 여부 (template_type 3)
  dualInstructor?: boolean;
  // 담당자 직위 라인 숨김 (template_type 4: 강사 1명·직위 없음)
  hideSignerTitle?: boolean;
  // 코스명 옆 (Xh) 시간 표기 숨김
  hideCourseHours?: boolean;
  // 날짜를 "Month DD, YYYY" 형태로 표기
  dateMonthName?: boolean;
  // 날짜 앞에 붙일 접두사 (예: "DATE | ")
  datePrefix?: string;
  // 로고를 중앙 배경 워터마크로 렌더 시 투명도 (예: 0.2)
  logoOpacity?: number;
  logo: ElementConfig;
  signature: ElementConfig;
  // 두 번째 강사 서명 (dualInstructor 전용)
  signature2?: ElementConfig;
  studentName: ElementConfig;
  courseName: ElementConfig;
  signerInfo: {
    container: ElementConfig;
    name: TextStyle;
    title: TextStyle;
  };
  // 두 번째 강사 이름 (dualInstructor 전용)
  signerInfo2?: {
    container: ElementConfig;
    name: TextStyle;
  };
  qrCode: {
    container: ElementConfig;
    layout?: 'horizontal' | 'vertical';
    text: TextStyle;
    // StampFit 도장 높이 (미지정 시 template 2와 동일한 큰 사이즈).
    // 도장 PNG는 상하 투명여백이 ~40%라 실제 보이는 도장 = sealHeight*0.6.
    // QR과 높이를 맞추려면 qrSize ≈ sealHeight*0.6 로 설정할 것.
    sealHeight?: string;
    // QR 박스 크기 (미지정 시 clamp(40px,4vw,60px)). 실제 도장(sealHeight*0.6)과 맞추는 용도
    qrSize?: string;
    // 고유번호를 도장 아래로 끌어올리는 비율 (sealHeight 대비, 기본 0.22).
    // 도장이 작아 QR과 높이가 비슷한 템플릿은 낮춰서 QR 침범 방지
    serialPullRatio?: number;
  };
  completionDate: ElementConfig;
  // 렌더링되는 "DATE" 고정 라벨 (배경에 baked 되지 않은 템플릿용). 강사명과 동일 스타일로 매칭
  dateLabel?: {
    container: ElementConfig;
    text: TextStyle;
  };
}

// 세로형(A4 세로) 템플릿 여부 — 미리보기 컨테이너 높이 분기 등에 사용
export function isPortraitTemplate(templateType: TemplateType): boolean {
  return TEMPLATE_CONFIGS[templateType]?.portrait === true;
}

// 강사 2명(서명·이름 2개) 템플릿 여부 — 모달의 강사2 입력/저장 게이팅에 사용
export function isDualInstructorTemplate(templateType: TemplateType): boolean {
  return TEMPLATE_CONFIGS[templateType]?.dualInstructor === true;
}

export const TEMPLATE_CONFIGS: Record<TemplateType, TemplateConfig> = {
  1: {
    backgroundImage: '/certificate-templates/template_1.png',
    hasBorder: true,
    logo: {
      position: {
        top: '8.5%',
        left: '33%',
      },
      size: {
        width: 'clamp(70px, 6vw, 80px)',
        height: 'auto',
        maxHeight: '70px',
      },
    },
    signature: {
      position: {
        bottom: '19%',
        left: '8%',
      },
      size: {
        width: 'clamp(150px, 18vw, 250px)',
        height: 'auto',
        maxHeight: '100px',
      },
    },
    studentName: {
      position: {
        top: '38%',
        left: '50%',
        transform: 'translateX(-50%)',
      },
      text: {
        fontFamily: 'Pretendard',
        fontWeight: 700,
        fontSize: 'clamp(28px, 4vw, 48px)',
        color: '#1a1a1a',
        letterSpacing: '0.02em',
      },
    },
    courseName: {
      position: {
        top: '58%',
        left: '50%',
        transform: 'translateX(-50%)',
      },
      text: {
        fontFamily: 'Pretendard',
        fontWeight: 600,
        fontStyle: 'italic',
        fontSize: 'clamp(20px, 2.8vw, 36px)',
        color: '#1a1a1a',
        letterSpacing: '0.01em',
      },
    },
    signerInfo: {
      container: {
        position: {
          left: '15%',
          bottom: '11%',
        },
      },
      name: {
        fontFamily: 'Pretendard',
        fontWeight: 500,
        fontSize: 'clamp(10px, 1vw, 18px)',
        color: '#1a1a1a',
        marginBottom: '2px',
      },
      title: {
        fontFamily: 'Pretendard',
        fontWeight: 400,
        fontSize: 'clamp(8px, 0.9vw, 14px)',
        color: '#1a1a1a',
      },
    },
    qrCode: {
      container: {
        position: {
          top: '5%',
          right: '5%',
        },
      },
      layout: 'vertical',
      text: {
        fontFamily: 'Pretendard',
        fontWeight: 400,
        fontSize: 'clamp(7px, 0.8vw, 10px)',
        color: '#6b7280',
        textAlign: 'center',
      },
    },
    completionDate: {
      position: {
        right: '17%',
        bottom: '18%',
      },
      text: {
        fontFamily: 'Pretendard',
        fontWeight: 500,
        fontSize: 'clamp(10px, 1.05vw, 18px)',
        color: '#1a1a1a',
      },
    },
  },
  2: {
    backgroundImage: '/certificate-templates/template_2.png',
    hasBorder: false,
    logo: {
      position: {
        top: '24%',
        left: '50%',
        transform: 'translateX(-50%)',
      },
      size: {
        width: 'clamp(60px, 8vw, 100px)',
        height: 'auto',
        maxHeight: '80px',
      },
    },
    signature: {
      position: {
        bottom: '24%',
        left: '74%',
        transform: 'translateX(-50%)',
      },
      size: {
        width: 'clamp(120px, 15vw, 200px)',
        height: 'auto',
        maxHeight: '80px',
      },
    },
    studentName: {
      position: {
        top: '43%',
        left: '50%',
        transform: 'translateX(-50%)',
      },
      text: {
        fontFamily: 'Pretendard',
        fontWeight: 200,
        fontSize: 'clamp(46px, 6.5vw, 73px)',
        color: '#1a1a1a',
        letterSpacing: '0.02em',
      },
    },
    courseName: {
      position: {
        top: '62%',
        left: '50%',
        transform: 'translateX(-50%)',
      },
      text: {
        fontFamily: 'Pretendard',
        fontWeight: 400,
        fontStyle: 'normal',
        fontSize: 'clamp(16px, 2.2vw, 28px)',
        color: '#1a1a1a',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
      },
    },
    signerInfo: {
      container: {
        position: {
          left: '74%',
          transform: 'translateX(-50%)',
          bottom: '17%',
        },
      },
      name: {
        fontFamily: 'Pretendard',
        fontWeight: 500,
        fontSize: 'clamp(11px, 1.1vw, 16px)',
        color: '#1a1a1a',
        marginBottom: '2px',
      },
      title: {
        fontFamily: 'Pretendard',
        fontWeight: 400,
        fontSize: 'clamp(9px, 0.95vw, 13px)',
        color: '#1a1a1a',
      },
    },
    qrCode: {
      container: {
        position: {
          left: '5%',
          bottom: '2%',
        },
      },
      text: {
        fontFamily: 'Pretendard',
        fontWeight: 400,
        fontSize: 'clamp(7px, 0.8vw, 10px)',
        color: '#6b7280',
        textAlign: 'left',
      },
    },
    completionDate: {
      position: {
        left: '23%',
        bottom: '24%',
      },
      text: {
        fontFamily: 'Pretendard',
        fontWeight: 500,
        fontSize: 'clamp(10px, 1.0vw, 16px)',
        color: '#1a1a1a',
      },
    },
  },
  // 템플릿 3: CERTIFICATE OF QUALIFICATION — 강사 2명(서명·이름 2개), 직위 없음
  // 모든 동적 콘텐츠는 cx 65.9% 정렬, 좌측 영역은 로고용. (template_3.png 1962x1387 분석 기준)
  3: {
    backgroundImage: '/certificate-templates/template_3.png',
    hasBorder: false,
    dualInstructor: true,
    hideCourseHours: true,
    dateMonthName: true,
    datePrefix: 'DATE | ',
    // 로고 — 좌측 영역 세로 중앙, 크게
    logo: {
      position: { top: '46%', left: '17%', transform: 'translate(-50%, -50%)' },
      size: { width: 'clamp(170px, 24vw, 330px)', height: 'auto', maxHeight: '270px' },
    },
    // 강사 1 서명 — 좌측 서명선(cx 45.8%, y 80.2%) 위
    signature: {
      position: { bottom: '21%', left: '45.8%', transform: 'translateX(-50%)' },
      size: { width: 'clamp(110px, 13vw, 180px)', height: 'auto', maxHeight: '70px' },
    },
    // 강사 2 서명 — 우측 서명선(cx 86%, y 80.2%) 위
    signature2: {
      position: { bottom: '21%', left: '86%', transform: 'translateX(-50%)' },
      size: { width: 'clamp(110px, 13vw, 180px)', height: 'auto', maxHeight: '70px' },
    },
    // 수강생 이름 — 밑줄(y 51%) 바로 위, cx 65.9% (크게)
    studentName: {
      position: { top: '41.5%', left: '65.9%', transform: 'translateX(-50%)' },
      size: { width: '56%' },
      text: {
        fontFamily: "'Google Sans Flex', 'Noto Sans KR', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(24px, 3.4vw, 46px)',
        color: '#1f2937',
        letterSpacing: '0.01em',
      },
    },
    // 코스명 — 타이틀 바로 아래 부제목 (대문자·레터스페이싱), cx 65.9%
    courseName: {
      position: { top: '26.5%', left: '65.9%', transform: 'translateX(-50%)' },
      size: { width: '74%' },
      text: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 600,
        fontSize: 'clamp(13px, 1.7vw, 22px)',
        color: '#4B5C79',
        letterSpacing: '0.3em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
    // 강사 1 이름 — 좌측 서명선 아래 (적당한 간격)
    signerInfo: {
      container: { position: { left: '45.8%', bottom: '14.5%', transform: 'translateX(-50%)' } },
      name: {
        fontFamily: "'Noto Sans', 'Noto Sans KR', sans-serif",
        fontWeight: 600,
        fontSize: 'clamp(11px, 1.2vw, 18px)',
        color: '#1f2937',
      },
      title: {
        fontFamily: "'Noto Sans', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(8px, 0.9vw, 14px)',
        color: '#6b7280',
      },
    },
    // 강사 2 이름 — 우측 서명선 아래 (적당한 간격)
    signerInfo2: {
      container: { position: { left: '86%', bottom: '14.5%', transform: 'translateX(-50%)' } },
      name: {
        fontFamily: "'Noto Sans', 'Noto Sans KR', sans-serif",
        fontWeight: 600,
        fontSize: 'clamp(11px, 1.2vw, 18px)',
        color: '#1f2937',
      },
    },
    // QR + 도장 — 우상단, 가로 배치(타이틀 안 덮도록)
    qrCode: {
      container: { position: { top: '4%', right: '4%' } },
      layout: 'horizontal',
      text: {
        fontFamily: "'Noto Sans', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(7px, 0.8vw, 10px)',
        color: '#6b7280',
        textAlign: 'center',
      },
    },
    // 날짜 — 우하단, "DATE | MAY 12, 2026" 형태
    completionDate: {
      position: { right: '5%', bottom: '5%' },
      text: {
        fontFamily: "'Noto Sans', 'Noto Sans KR', sans-serif",
        fontWeight: 700,
        fontSize: 'clamp(12px, 1.4vw, 19px)',
        color: '#1f2937',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        textAlign: 'right',
      },
    },
  },
  // 템플릿 4: CERTIFICATE OF QUALIFICATION — 강사 1명·직위 없음, 로고 하단 중앙
  // (template_4_bg.png 1964x1390 + exam 분석 기준. 본문은 cx 50% 정렬)
  4: {
    backgroundImage: '/certificate-templates/template_4.png',
    hasBorder: false,
    hideSignerTitle: true,
    hideCourseHours: true,
    dateMonthName: true,
    // 로고 — 하단 중앙 원형 (두 서명선 사이), exam 도장 위치/크기
    logo: {
      position: { top: '82%', left: '50%', transform: 'translate(-50%, -50%)' },
      size: { width: 'clamp(100px, 13vw, 185px)', height: 'auto', maxHeight: '150px' },
    },
    // 강사 서명 — 좌하단 선(cx 18.5%, y 85.7%) 위
    signature: {
      position: { bottom: '16%', left: '18.5%', transform: 'translateX(-50%)' },
      size: { width: 'clamp(110px, 13vw, 190px)', height: 'auto', maxHeight: '75px' },
    },
    // 수강생 이름 — 밑줄(y 52.4%) 위, cx 50%, 대형 (살짝 위로)
    studentName: {
      position: { top: '42.2%', left: '50%', transform: 'translateX(-50%)' },
      size: { width: '62%' },
      text: {
        fontFamily: "'Google Sans Flex', 'Noto Sans KR', sans-serif",
        fontWeight: 500,
        fontSize: 'clamp(26px, 5.2vw, 60px)',
        color: '#101324',
        letterSpacing: '0.01em',
      },
    },
    // 코스명 — 타이틀 아래 부제목(대문자·레터스페이싱), cx 50%
    courseName: {
      position: { top: '36%', left: '50%', transform: 'translateX(-50%)' },
      size: { width: '80%' },
      text: {
        fontFamily: "'Noto Sans', 'Noto Sans KR', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(14px, 1.9vw, 26px)',
        color: '#4C5D7B',
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
    // 강사 이름 또는 직책 — 좌하단 선 아래
    signerInfo: {
      container: { position: { left: '18.5%', bottom: '9.5%', transform: 'translateX(-50%)' } },
      name: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 200,
        fontSize: 'clamp(10px, 1.15vw, 17px)',
        color: '#101324',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      },
      title: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 200,
        fontSize: 'clamp(8px, 0.9vw, 14px)',
        color: '#6b7280',
      },
    },
    // QR + 도장 — 우상단 가로 배치 (template 3과 동일)
    qrCode: {
      container: { position: { top: '4%', right: '4%' } },
      layout: 'horizontal',
      text: {
        fontFamily: "'Noto Sans', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(7px, 0.8vw, 10px)',
        color: '#6b7280',
        textAlign: 'center',
      },
    },
    // 날짜 — 우하단 선(cx 81.9%, y 85.7%) 위, "MAY 12, 2026"
    completionDate: {
      position: { bottom: '15.5%', left: '81.9%', transform: 'translateX(-50%)' },
      text: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 200,
        fontSize: 'clamp(11px, 1.35vw, 19px)',
        color: '#101324',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
  },
  // 템플릿 5: 강사 2명·직위 없음, serif 대형(코스명·수강생명), 로고 하단 중앙
  // (template_5_bg.png 1962x1388 + exam 분석. 본문 cx 50%, 하단 선 cx 23.8%/76.1%)
  5: {
    backgroundImage: '/certificate-templates/template_5.png',
    hasBorder: false,
    dualInstructor: true,
    hideCourseHours: true,
    dateMonthName: true,
    datePrefix: 'DATE | ',
    // 로고 — 하단 중앙 원형 (두 서명선 사이), exam 도장 위치/크기
    logo: {
      position: { top: '82%', left: '50%', transform: 'translate(-50%, -50%)' },
      size: { width: 'clamp(100px, 13vw, 185px)', height: 'auto', maxHeight: '150px' },
    },
    // 강사 1 서명 — 좌 서명선(cx 23.8%, y 84.4%) 위
    signature: {
      position: { bottom: '16.5%', left: '23.8%', transform: 'translateX(-50%)' },
      size: { width: 'clamp(110px, 13vw, 180px)', height: 'auto', maxHeight: '70px' },
    },
    // 강사 2 서명 — 우 서명선(cx 76.1%, y 84.4%) 위
    signature2: {
      position: { bottom: '16.5%', left: '76.1%', transform: 'translateX(-50%)' },
      size: { width: 'clamp(110px, 13vw, 180px)', height: 'auto', maxHeight: '70px' },
    },
    // 수강생 이름 — 밑줄(y 54.2%) 위, cx 50%, 대형 serif
    studentName: {
      position: { top: '42.5%', left: '50%', transform: 'translateX(-50%)' },
      size: { width: '76%' },
      text: {
        fontFamily: "'Noto Serif KR', serif",
        fontWeight: 200,
        fontSize: 'clamp(30px, 6.2vw, 66px)',
        color: '#2C3245',
        letterSpacing: '0.01em',
      },
    },
    // 코스명 — 타이틀 아래 대형 serif(대문자), cx 50%
    courseName: {
      position: { top: '21%', left: '50%', transform: 'translateX(-50%)' },
      size: { width: '86%' },
      text: {
        fontFamily: "'Noto Serif KR', serif",
        fontWeight: 200,
        fontSize: 'clamp(28px, 5.4vw, 58px)',
        color: '#2C3245',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
    // 강사 1 이름 또는 직책 — 좌 서명선 아래
    signerInfo: {
      container: { position: { left: '23.8%', bottom: '12%', transform: 'translateX(-50%)' } },
      name: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 200,
        fontSize: 'clamp(10px, 1.15vw, 17px)',
        color: '#15152B',
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
      },
      title: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 200,
        fontSize: 'clamp(8px, 0.9vw, 14px)',
        color: '#6b7280',
      },
    },
    // 강사 2 이름 또는 직책 — 우 서명선 아래
    signerInfo2: {
      container: { position: { left: '76.1%', bottom: '12%', transform: 'translateX(-50%)' } },
      name: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 200,
        fontSize: 'clamp(10px, 1.15vw, 17px)',
        color: '#15152B',
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
      },
    },
    // QR + 도장 — 우상단 가로 배치 (template 3과 동일)
    qrCode: {
      container: { position: { top: '5%', right: '5%' } },
      layout: 'horizontal',
      text: {
        fontFamily: "'Noto Sans', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(7px, 0.8vw, 10px)',
        color: '#6b7280',
        textAlign: 'center',
      },
    },
    // 날짜 — 우하단 "DATE | MAY 12, 2026" (Noto Sans regular)
    completionDate: {
      position: { right: '9%', bottom: '6.5%' },
      text: {
        fontFamily: "'Noto Sans', 'Noto Sans KR', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(11px, 1.35vw, 19px)',
        color: '#101324',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        textAlign: 'right',
      },
    },
  },
  // 템플릿 6: CERTIFICATE OF PARTICIPATION — 강사 1명·직위 없음, 수강생명 손글씨(Charm)
  // (template_6_bg.png 1962x1387 + exam 분석. 본문 cx 50%, 하단 선 cx 17%/82.9%)
  6: {
    backgroundImage: '/certificate-templates/template_6.png',
    hasBorder: false,
    hideSignerTitle: true,
    hideCourseHours: true,
    dateMonthName: true,
    // 로고 — 하단 중앙 원형 (두 서명선 사이), exam 도장 위치/크기
    logo: {
      position: { top: '80%', left: '50%', transform: 'translate(-50%, -50%)' },
      size: { width: 'clamp(100px, 13vw, 185px)', height: 'auto', maxHeight: '150px' },
    },
    // 강사 서명 — 좌 서명선(cx 17%, y 81.9%) 위
    signature: {
      position: { bottom: '20%', left: '17%', transform: 'translateX(-50%)' },
      size: { width: 'clamp(110px, 13vw, 190px)', height: 'auto', maxHeight: '75px' },
    },
    // 수강생 이름 — 밑줄(y 47.7%) 위, cx 50%, 손글씨 대형
    studentName: {
      position: { top: '29.5%', left: '50%', transform: 'translateX(-50%)' },
      size: { width: '76%' },
      text: {
        fontFamily: "'Charm', 'Noto Sans KR', cursive",
        fontWeight: 400,
        fontSize: 'clamp(34px, 6.4vw, 68px)',
        color: '#16152B',
        letterSpacing: '0.01em',
      },
    },
    // 코스명 — 타이틀 아래 부제목(대문자·레터스페이싱), cx 50%
    courseName: {
      position: { top: '22.5%', left: '50%', transform: 'translateX(-50%)' },
      size: { width: '80%' },
      text: {
        fontFamily: "'Poppins', 'Noto Sans KR', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(13px, 1.7vw, 23px)',
        color: '#4C5D7B',
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
    // 강사 이름 또는 직책 — 좌 서명선 아래 (우측 baked "DATE" 라벨과 동일 높이)
    signerInfo: {
      container: { position: { left: '17%', bottom: '13%', transform: 'translateX(-50%)' } },
      name: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 200,
        fontSize: 'clamp(10px, 1.15vw, 17px)',
        color: '#16152B',
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
      },
      title: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 200,
        fontSize: 'clamp(8px, 0.9vw, 14px)',
        color: '#6b7280',
      },
    },
    // QR + 도장 — 우상단 가로 배치. 전폭 타이틀 위 좁은 여백이라 도장은 중간 크기
    qrCode: {
      container: { position: { top: '2%', right: '4%' } },
      layout: 'horizontal',
      sealHeight: 'clamp(48px, 6vw, 68px)',
      text: {
        fontFamily: "'Noto Sans', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(7px, 0.8vw, 10px)',
        color: '#6b7280',
        textAlign: 'center',
      },
    },
    // 날짜 — 우 서명선(cx 82.9%, y 81.9%) 위, "MAY 12, 2026"
    completionDate: {
      position: { bottom: '20%', left: '82.9%', transform: 'translateX(-50%)' },
      text: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 200,
        fontSize: 'clamp(11px, 1.35vw, 19px)',
        color: '#16152B',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
  },
  // 템플릿 7: 다크 네이비 배경 + 골드, 강사 1명, 로고 중앙 워터마크(투명도 20%)
  // (template_7_bg.png 1962x1388 + exam 분석. 본문 cx 50%, 하단 선 cx 16.1%/84.8%)
  7: {
    backgroundImage: '/certificate-templates/template_7.png',
    hasBorder: false,
    hideSignerTitle: true,
    hideCourseHours: true,
    dateMonthName: true,
    logoOpacity: 0.2,
    // 로고 — 중앙 배경 워터마크 (크게, 투명도 20%)
    logo: {
      position: { top: '48%', left: '50%', transform: 'translate(-50%, -50%)' },
      size: { width: 'clamp(280px, 42vw, 760px)', height: 'auto', maxHeight: '440px' },
    },
    // 강사 서명 — 좌 서명선(cx 16.1%, y 86.8%) 위
    signature: {
      position: { bottom: '15%', left: '16.1%', transform: 'translateX(-50%)' },
      size: { width: 'clamp(110px, 13vw, 190px)', height: 'auto', maxHeight: '75px' },
    },
    // 수강생 이름 — presented to(y37%)와 divider(y53.5%) 사이, cx 50%, 대형(Montserrat bold, 흰색)
    studentName: {
      position: { top: '42%', left: '50%', transform: 'translateX(-50%)' },
      size: { width: '84%' },
      text: {
        fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif",
        fontWeight: 700,
        fontSize: 'clamp(26px, 4.6vw, 52px)',
        color: '#FFFFFF',
        letterSpacing: '0.01em',
      },
    },
    // 코스명 — 타이틀 아래 대형 serif(대문자, 흰색 bold)
    courseName: {
      position: { top: '23%', left: '50%', transform: 'translateX(-50%)' },
      size: { width: '86%' },
      text: {
        fontFamily: "'Noto Serif KR', serif",
        fontWeight: 700,
        fontSize: 'clamp(28px, 5vw, 56px)',
        color: '#FFFFFF',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
    // 강사 이름 또는 직책 — 좌 서명선 아래 (우측 baked "DATE" 라벨과 동일 높이)
    signerInfo: {
      container: { position: { left: '16.1%', bottom: '8%', transform: 'translateX(-50%)' } },
      name: {
        fontFamily: "'Poppins', 'Noto Sans KR', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(10px, 1.15vw, 17px)',
        color: '#FFFFFF',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
      },
      title: {
        fontFamily: "'Poppins', 'Noto Sans KR', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(8px, 0.9vw, 14px)',
        color: '#cbd5e1',
      },
    },
    // QR + 도장 — 우상단 가로 배치 (template 3과 동일). 다크 배경이라 일련번호는 밝은 회색
    qrCode: {
      container: { position: { top: '4%', right: '4%' } },
      layout: 'horizontal',
      text: {
        fontFamily: "'Noto Sans', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(7px, 0.8vw, 10px)',
        color: '#cbd5e1',
        textAlign: 'center',
      },
    },
    // 날짜 — 우 서명선(cx 84.8%, y 86.8%) 위, "MAY 12, 2026" (골드, Poppins medium)
    completionDate: {
      position: { bottom: '15%', left: '84.8%', transform: 'translateX(-50%)' },
      text: {
        fontFamily: "'Poppins', 'Noto Sans KR', sans-serif",
        fontWeight: 500,
        fontSize: 'clamp(11px, 1.4vw, 20px)',
        color: '#D5A738',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
  },
  // 템플릿 8: CERTIFICATE OF COMPLETION — 크림 배경, 강사 1명, 얇은 폰트(thin), 로고 하단 중앙
  // (template_8_bg.png 1962x1388 + exam 분석. 본문 cx 50%, 하단 선 cx 20.9%/79.1%)
  8: {
    backgroundImage: '/certificate-templates/template_8.png',
    hasBorder: false,
    hideSignerTitle: true,
    hideCourseHours: true,
    dateMonthName: true,
    // 로고 — 하단 중앙 원형 (두 서명선 사이), exam 도장 위치/크기
    logo: {
      position: { top: '83%', left: '50%', transform: 'translate(-50%, -50%)' },
      size: { width: 'clamp(95px, 12vw, 175px)', height: 'auto', maxHeight: '135px' },
    },
    // 강사 서명 — 좌 서명선(cx 20.9%, y 86.8%) 위
    signature: {
      position: { bottom: '14.5%', left: '20.9%', transform: 'translateX(-50%)' },
      size: { width: 'clamp(110px, 13vw, 190px)', height: 'auto', maxHeight: '75px' },
    },
    // 수강생 이름 — 골드 밑줄(y 56.3%) 위, cx 50%, 대형 serif(extralight)
    studentName: {
      position: { top: '43%', left: '50%', transform: 'translateX(-50%)' },
      size: { width: '76%' },
      text: {
        fontFamily: "'Noto Serif KR', serif",
        fontWeight: 200,
        fontSize: 'clamp(30px, 6vw, 64px)',
        color: '#15152B',
        letterSpacing: '0.01em',
      },
    },
    // 코스명 — 타이틀 아래 부제목(대문자·레터스페이싱), cx 50%, 얇게(thin)
    courseName: {
      position: { top: '22.5%', left: '50%', transform: 'translateX(-50%)' },
      size: { width: '80%' },
      text: {
        fontFamily: "'Noto Sans', 'Noto Sans KR', sans-serif",
        fontWeight: 100,
        fontSize: 'clamp(13px, 1.7vw, 23px)',
        color: '#15152B',
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
    // 강사 이름 또는 직책 — 좌 서명선 아래 (thin)
    // 데스크탑에서 ~15px로 안정화(baked "DATE" 라벨과 동일 크기)해 우측 DATE와 크기·수평 정렬.
    // vw만 쓰면 큰 화면에서 폰트가 계속 커져 어긋나므로 상한(15px)으로 안정화
    signerInfo: {
      container: { position: { left: '20.9%', bottom: '8.6%', transform: 'translateX(-50%)' } },
      name: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 100,
        fontSize: 'clamp(12px, 1.5vw, 15px)',
        color: '#15152B',
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
      },
      title: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 100,
        fontSize: 'clamp(8px, 0.9vw, 14px)',
        color: '#6b7280',
      },
    },
    // QR + 도장 — 우상단 가로 배치. 전폭 타이틀 위 좁은 여백이라 도장은 중간 크기
    qrCode: {
      container: { position: { top: '1%', right: '4%' } },
      layout: 'horizontal',
      sealHeight: 'clamp(48px, 6vw, 68px)',
      text: {
        fontFamily: "'Noto Sans', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(7px, 0.8vw, 10px)',
        color: '#6b7280',
        textAlign: 'center',
      },
    },
    // 날짜 — 우 서명선(cx 79.1%, y 86.8%) 위, "MAY 12, 2026" (thin)
    completionDate: {
      position: { bottom: '15%', left: '79.1%', transform: 'translateX(-50%)' },
      text: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 100,
        fontSize: 'clamp(11px, 1.35vw, 19px)',
        color: '#15152B',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
  },
  // 템플릿 9: 아르데코, 검은 상단 밴드 + 크림 본문, 강사 1명. 로고 좌상단 워터마크
  // (template_9_bg.png 1962x1387 + exam. "DATE"는 baked, 강사명은 이에 맞춤. 하단 좌 cx23.4%/우 cx76.6%)
  9: {
    backgroundImage: '/certificate-templates/template_9.png',
    hasBorder: false,
    hideSignerTitle: true,
    hideCourseHours: true,
    dateMonthName: true,
    logoOpacity: 0.18,
    // 로고 — 좌상단 반투명 워터마크 (작게, 상단 살짝 내림). 좌상단 앵커
    logo: {
      position: { top: '7%', left: '4%' },
      size: { width: 'clamp(130px, 20vw, 220px)', height: 'auto', maxHeight: '210px' },
    },
    // 강사 서명 — 좌하단(cx 23.5%), 서명선(y 82.9%) 위 (날짜값과 바닥 정렬)
    signature: {
      position: { bottom: '18%', left: '23.5%', transform: 'translateX(-50%)' },
      size: { width: 'clamp(110px, 13vw, 185px)', height: 'auto', maxHeight: '72px' },
    },
    // 수강생 이름 — 중앙 데코선(y53%) 위에 놓이도록 아래로, cx 50%, 손글씨(Charm) 대형
    studentName: {
      position: { top: '42%', left: '50%', transform: 'translateX(-50%)' },
      size: { width: '82%' },
      text: {
        fontFamily: "'Charm', 'Noto Sans KR', cursive",
        fontWeight: 400,
        fontSize: 'clamp(38px, 7.5vw, 82px)',
        color: '#151413',
        letterSpacing: '0.01em',
      },
    },
    // 코스명 — 검은 상단 밴드, 삼각형 마크 사이(y21-23%), cx 50%, Noto Sans light(흰색)
    courseName: {
      position: { top: '19.5%', left: '50%', transform: 'translateX(-50%)' },
      size: { width: '72%' },
      text: {
        fontFamily: "'Noto Sans', 'Noto Sans KR', sans-serif",
        fontWeight: 300,
        fontSize: 'clamp(13px, 1.7vw, 24px)',
        color: '#F7EEE1',
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
    // 강사 이름 또는 직책 — 좌하단(cx 23.4%), baked "DATE"(cx76.5%, center y86.1%)와 동일 크기·높이
    // Noto Serif KR bold. 데스크탑에서 18px 안정화(큰 화면 어긋남 방지)
    signerInfo: {
      container: { position: { left: '23.4%', bottom: '12.3%', transform: 'translateX(-50%)' } },
      name: {
        fontFamily: "'Noto Serif KR', serif",
        fontWeight: 700,
        fontSize: 'clamp(14px, 1.8vw, 18px)',
        color: '#000000',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
      },
      title: {
        fontFamily: "'Noto Serif KR', serif",
        fontWeight: 700,
        fontSize: 'clamp(12px, 1.5vw, 16px)',
        color: '#6b7280',
      },
    },
    // QR + 도장 — 검은 밴드/전폭 타이틀을 피해 크림 본문 우상단에 배치 (살짝 내림)
    qrCode: {
      container: { position: { top: '37%', right: '5%' } },
      layout: 'horizontal',
      text: {
        fontFamily: "'Noto Sans', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(7px, 0.8vw, 10px)',
        color: '#6b7280',
        textAlign: 'center',
      },
    },
    // 날짜값 — 우하단(cx 76.6%), 서명선(y 82.9%) 위 (서명과 바닥 정렬), "MAY 12, 2026"
    completionDate: {
      position: { bottom: '18%', left: '76.6%', transform: 'translateX(-50%)' },
      text: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 100,
        fontSize: 'clamp(12px, 1.5vw, 18px)',
        color: '#15152B',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
  },
  // 템플릿 10: CERTIFICATE OF COMPLETION — 크림+골드 오너먼트, 강사 1명, 로고 중앙하단
  // (template_10_bg 1958x1384 + exam. "DATE"는 렌더(강사명과 동일). 하단 좌 cx20%/우 cx79.9%)
  10: {
    backgroundImage: '/certificate-templates/template_10.png',
    hasBorder: false,
    hideSignerTitle: true,
    hideCourseHours: true,
    dateMonthName: true,
    // 로고 — 중앙 하단 (baked STAMP FIT 도장 위치/크기), exam cx50% cy83.5%
    logo: {
      position: { top: '83.5%', left: '50%', transform: 'translate(-50%, -50%)' },
      size: { width: 'clamp(100px, 13vw, 175px)', height: 'auto', maxHeight: '145px' },
    },
    // 강사 서명 — 좌하단(cx 20%), 날짜값과 바닥 정렬(bottom 18%)
    signature: {
      position: { bottom: '18%', left: '20%', transform: 'translateX(-50%)' },
      size: { width: 'clamp(110px, 13vw, 185px)', height: 'auto', maxHeight: '72px' },
    },
    // 수강생 이름 — 흰 데코선(y58.6%) 위, cx 50%, Noto Serif KR extralight 대형
    studentName: {
      position: { top: '42%', left: '50%', transform: 'translateX(-50%)' },
      size: { width: '80%' },
      text: {
        fontFamily: "'Noto Serif KR', serif",
        fontWeight: 200,
        fontSize: 'clamp(30px, 6vw, 64px)',
        color: '#6F6255',
        letterSpacing: '0.01em',
      },
    },
    // 코스명 — 타이틀 아래 부제목, cx 50%, Noto Serif Medium(회색)
    courseName: {
      position: { top: '37%', left: '50%', transform: 'translateX(-50%)' },
      size: { width: '78%' },
      text: {
        fontFamily: "'Noto Serif', 'Noto Serif KR', serif",
        fontWeight: 500,
        fontSize: 'clamp(15px, 2vw, 28px)',
        color: '#7D7975',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
    // 강사 이름 또는 직책 — 좌하단(cx 20%), Noto Serif KR extralight. DATE와 동일 크기·높이
    signerInfo: {
      container: { position: { left: '20%', bottom: '12%', transform: 'translateX(-50%)' } },
      name: {
        fontFamily: "'Noto Serif KR', serif",
        fontWeight: 200,
        fontSize: 'clamp(14px, 1.8vw, 18px)',
        color: '#000000',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
      },
      title: {
        fontFamily: "'Noto Serif KR', serif",
        fontWeight: 200,
        fontSize: 'clamp(12px, 1.5vw, 15px)',
        color: '#6b7280',
      },
    },
    // QR + 도장 — 우상단 가로 배치
    qrCode: {
      container: { position: { top: '5%', right: '5%' } },
      layout: 'horizontal',
      text: {
        fontFamily: "'Noto Sans', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(7px, 0.8vw, 10px)',
        color: '#6b7280',
        textAlign: 'center',
      },
    },
    // 날짜값 — 우하단(cx 79.9%), 서명과 바닥 정렬(bottom 18%), "MAY 12, 2026" (Noto Sans KR extralight)
    completionDate: {
      position: { bottom: '18%', left: '79.9%', transform: 'translateX(-50%)' },
      text: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 200,
        fontSize: 'clamp(12px, 1.5vw, 18px)',
        color: '#3a3a3a',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
    // "DATE" 라벨 — 우하단(cx 79.9%), 날짜값 아래. 강사명과 동일(Noto Serif KR extralight, 동일 크기·높이)
    dateLabel: {
      container: { position: { left: '79.9%', bottom: '12%', transform: 'translateX(-50%)' } },
      text: {
        fontFamily: "'Noto Serif KR', serif",
        fontWeight: 200,
        fontSize: 'clamp(14px, 1.8vw, 18px)',
        color: '#000000',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
  },
  // 템플릿 11: 흰 배경 + 좌측 웨이브, 강사 1명. 로고 중앙 워터마크, "DATE"는 날짜값 위에 렌더
  // (template_11 1958x1382. 콘텐츠 cx~56.5%. 하단쌍: DATE cx40%/INSTRUCTOR cx60% 라벨 위 + 날짜값/서명 아래)
  11: {
    backgroundImage: '/certificate-templates/template_11.png',
    hasBorder: false,
    hideSignerTitle: true,
    hideCourseHours: true,
    dateMonthName: true,
    logoOpacity: 0.14,
    // 로고 — 콘텐츠 중앙 워터마크 (수강생명 근처, 적당히 크게)
    logo: {
      position: { top: '51%', left: '56%', transform: 'translate(-50%, -50%)' },
      size: { width: 'clamp(200px, 34vw, 460px)', height: 'auto', maxHeight: '48%' },
    },
    // 강사 서명 — 우측 열(cx 60%), 강사명 아래. 날짜값과 수직 중앙 정렬(center y86.6%)
    signature: {
      position: { top: '86.6%', left: '60%', transform: 'translate(-50%, -50%)' },
      size: { width: 'clamp(110px, 13vw, 185px)', height: 'auto', maxHeight: '68px' },
    },
    // 수강생 이름 — 밑줄(y52%) 위, cx 56.5%, Bonheur Royale script 대형
    studentName: {
      position: { top: '37%', left: '56.5%', transform: 'translateX(-50%)' },
      size: { width: '62%' },
      text: {
        fontFamily: "'Bonheur Royale', 'Noto Serif KR', cursive",
        fontWeight: 400,
        fontSize: 'clamp(42px, 8.5vw, 92px)',
        color: '#1F1531',
        letterSpacing: '0.01em',
      },
    },
    // 코스명 — 타이틀 아래, cx 56.5%, Noto Serif KR bold(대문자)
    courseName: {
      position: { top: '31%', left: '56.5%', transform: 'translateX(-50%)' },
      size: { width: '58%' },
      text: {
        fontFamily: "'Noto Serif KR', serif",
        fontWeight: 700,
        fontSize: 'clamp(15px, 2.2vw, 30px)',
        color: '#1A1F3D',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
    // 강사 이름 또는 직책 — 우측 열(cx 60%) 위, DATE와 동일 크기·높이. Noto Serif KR extralight
    signerInfo: {
      container: { position: { left: '60%', bottom: '19%', transform: 'translateX(-50%)' } },
      name: {
        fontFamily: "'Noto Serif KR', serif",
        fontWeight: 200,
        fontSize: 'clamp(14px, 1.8vw, 18px)',
        color: '#242939',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
      },
      title: {
        fontFamily: "'Noto Serif KR', serif",
        fontWeight: 200,
        fontSize: 'clamp(12px, 1.5vw, 15px)',
        color: '#6b7280',
      },
    },
    // QR + 도장 — 이 템플릿만 좌상단 배치
    qrCode: {
      container: { position: { top: '4%', left: '4%' } },
      layout: 'horizontal',
      text: {
        fontFamily: "'Noto Sans', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(7px, 0.8vw, 10px)',
        color: '#6b7280',
        textAlign: 'center',
      },
    },
    // 날짜값 — 좌측 열(cx 40%), DATE 라벨 아래. 서명과 수직 중앙 정렬. Noto Serif KR extralight
    completionDate: {
      position: { bottom: '12%', left: '40%', transform: 'translateX(-50%)' },
      text: {
        fontFamily: "'Noto Serif KR', serif",
        fontWeight: 200,
        fontSize: 'clamp(15px, 1.9vw, 23px)',
        color: '#242939',
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
    // "DATE" 라벨 — 좌측 열(cx 40%), 날짜값 위. 강사명과 동일(Noto Serif KR extralight, 동일 크기·높이)
    dateLabel: {
      container: { position: { left: '40%', bottom: '19%', transform: 'translateX(-50%)' } },
      text: {
        fontFamily: "'Noto Serif KR', serif",
        fontWeight: 200,
        fontSize: 'clamp(14px, 1.8vw, 18px)',
        color: '#242939',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
  },
  // 템플릿 12: 라이트그레이 + 네이비/골드 코너, 강사 1명. 로고 우하단 워터마크
  // (template_12 1958x1387. 콘텐츠 cx50%. 하단: 날짜값/DATE 좌 cx32%, 서명/INSTRUCTOR 우 cx64%)
  12: {
    backgroundImage: '/certificate-templates/template_12.png',
    hasBorder: false,
    hideSignerTitle: true,
    hideCourseHours: true,
    dateMonthName: true,
    logoOpacity: 0.15,
    // 로고 — 우하단 워터마크 (exam STAMP FIT 도장 위치/크기: cx86.7% cy73% h32%)
    logo: {
      position: { top: '73%', left: '86.7%', transform: 'translate(-50%, -50%)' },
      size: { width: 'clamp(160px, 26vw, 340px)', height: 'auto', maxHeight: '34%' },
    },
    // 강사 서명 — 우측 열(밑줄 cx 67.8%), 밑줄 바로 위. 날짜값과 바닥 정렬(bottom 14%)
    signature: {
      position: { bottom: '14%', left: '67.8%', transform: 'translateX(-50%)' },
      size: { width: 'clamp(110px, 13vw, 185px)', height: 'auto', maxHeight: '70px' },
    },
    // 수강생 이름 — 밑줄(y62%) 위, cx 50%, 손글씨(Charm) 대형
    studentName: {
      position: { top: '40%', left: '50%', transform: 'translateX(-50%)' },
      size: { width: '76%' },
      text: {
        fontFamily: "'Charm', 'Noto Sans KR', cursive",
        fontWeight: 400,
        fontSize: 'clamp(40px, 8vw, 88px)',
        color: '#2C4069',
        letterSpacing: '0.01em',
      },
    },
    // 코스명 — 타이틀 아래, cx 50%, Noto Serif KR extralight(대문자)
    courseName: {
      position: { top: '33.5%', left: '50%', transform: 'translateX(-50%)' },
      size: { width: '60%' },
      text: {
        fontFamily: "'Noto Serif KR', serif",
        fontWeight: 200,
        fontSize: 'clamp(15px, 2vw, 28px)',
        color: '#2C4069',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
    // 강사 이름 또는 직책 — 우측 열(밑줄 cx 67.8%), 밑줄 아래. DATE와 동일. Noto Sans KR thin
    signerInfo: {
      container: { position: { left: '67.8%', bottom: '8%', transform: 'translateX(-50%)' } },
      name: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 100,
        fontSize: 'clamp(12px, 1.5vw, 18px)',
        color: '#2C4069',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      },
      title: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 100,
        fontSize: 'clamp(9px, 1.1vw, 13px)',
        color: '#6b7280',
      },
    },
    // QR + 도장 — 우상단 가로 배치. 네이비 코너 위라 일련번호는 밝은 색
    qrCode: {
      container: { position: { top: '4%', right: '4%' } },
      layout: 'horizontal',
      text: {
        fontFamily: "'Noto Sans', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(7px, 0.8vw, 10px)',
        color: '#cbd5e1',
        textAlign: 'center',
      },
    },
    // 날짜값 — 좌측 열(밑줄 cx 32%), 밑줄 바로 위. 서명과 바닥 정렬(bottom 14%). Noto Sans KR thin
    completionDate: {
      position: { bottom: '14%', left: '32%', transform: 'translateX(-50%)' },
      text: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 100,
        fontSize: 'clamp(12px, 1.5vw, 19px)',
        color: '#2C4069',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
    // "DATE" 라벨 — 좌측 열(밑줄 cx 32%), 밑줄 아래. 강사명과 동일(Noto Sans KR thin, 동일 크기·높이)
    dateLabel: {
      container: { position: { left: '32%', bottom: '8%', transform: 'translateX(-50%)' } },
      text: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 100,
        fontSize: 'clamp(12px, 1.5vw, 18px)',
        color: '#2C4069',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
  },
  // 템플릿 13: 세로형(A4 세로) CERTIFICATE OF COMPLETION — 강사 1명, 로고 우하단
  // (template_13 1385x1958 세로. 콘텐츠 cx50%. 블루밑줄 y53.8%, 서명선 y79.1%/cx30.8%)
  13: {
    backgroundImage: '/certificate-templates/template_13.png',
    hasBorder: false,
    portrait: true,
    hideSignerTitle: true,
    hideCourseHours: true,
    dateMonthName: true,
    datePrefix: 'DATE | ',
    // 기업 로고 — 우하단, exam STAMP FIT 도장 위치/크기 (ctr 71.8%,78.9% 지름~150px)
    logo: {
      position: { top: '78.9%', left: '71.8%', transform: 'translate(-50%, -50%)' },
      size: { width: 'clamp(90px, 20vw, 150px)', height: 'auto', maxHeight: '15%' },
    },
    // 강사 서명 — 좌측 서명선(cx 30.8%, y 79.1%) 바로 위
    signature: {
      position: { bottom: '22%', left: '30.8%', transform: 'translateX(-50%)' },
      size: { width: 'clamp(90px, 18vw, 150px)', height: 'auto', maxHeight: '52px' },
    },
    // 수강생 이름 — 블루밑줄(y53.8%) 바로 위, cx 50%. Montserrat thin
    studentName: {
      position: { top: '50.9%', left: '50%', transform: 'translate(-50%, -50%)' },
      size: { width: '78%' },
      text: {
        fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif",
        fontWeight: 100,
        fontSize: 'clamp(22px, 3.2vw, 43px)',
        color: '#1f2937',
        letterSpacing: '0.01em',
        textAlign: 'center',
      },
    },
    // 코스명 — 메달 아래 부제목(대문자·넓은 자간), cx 50%. Noto Sans KR thin
    courseName: {
      position: { top: '37.1%', left: '50%', transform: 'translate(-50%, -50%)' },
      size: { width: '80%' },
      text: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 100,
        fontSize: 'clamp(13px, 1.85vw, 25px)',
        color: '#3a4a63',
        letterSpacing: '0.25em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
    // 강사 이름 또는 직책 — 좌측 서명선 아래(ctr 81.4%). Montserrat thin
    signerInfo: {
      container: { position: { left: '30.8%', bottom: '18%', transform: 'translateX(-50%)' } },
      name: {
        fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif",
        fontWeight: 100,
        fontSize: 'clamp(10px, 1.1vw, 15px)',
        color: '#4a5568',
        letterSpacing: '0.15em',
        textTransform: 'uppercase',
      },
      title: {
        fontFamily: "'Montserrat', sans-serif",
        fontWeight: 100,
        fontSize: 'clamp(8px, 0.9vw, 13px)',
        color: '#6b7280',
      },
    },
    // QR + 도장 — 우상단, 가로 배치 (template 3 스타일). 세로형 테두리 안쪽으로 넣어 프레임과 안 겹치게
    qrCode: {
      container: { position: { top: '10.5%', right: '10%' } },
      layout: 'horizontal',
      // 실제 도장(=sealHeight*0.6) ≈ qrSize 로 QR과 도장 높이 매칭
      qrSize: 'clamp(34px, 3.6vw, 46px)',
      sealHeight: 'clamp(57px, 6vw, 77px)',
      serialPullRatio: 0.04,
      text: {
        fontFamily: "'Noto Sans', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(7px, 0.8vw, 10px)',
        color: '#6b7280',
        textAlign: 'center',
      },
    },
    // 날짜 — 좌하단, "DATE | MAY 12, 2026" (ctr y87.1%). Noto Sans semibold
    completionDate: {
      position: { left: '15.5%', bottom: '12%' },
      text: {
        fontFamily: "'Noto Sans', 'Noto Sans KR', sans-serif",
        fontWeight: 600,
        fontSize: 'clamp(12px, 1.35vw, 18px)',
        color: '#1f2937',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        textAlign: 'left',
      },
    },
  },
  // 템플릿 14: 세로형(A4 세로) CERTIFICATE OF COMPLETION — 강사 2명(template 3 구조), 로고 우하단
  // (template_14 1385x1958 세로. 콘텐츠 cx50%. 이름박스 y41.5-47.3%, 서명선 cx28.3% y79.9%/93.3%)
  14: {
    backgroundImage: '/certificate-templates/template_14.png',
    hasBorder: false,
    portrait: true,
    dualInstructor: true,
    hideCourseHours: true,
    dateMonthName: true,
    // 기업 로고 — 우하단, exam STAMP FIT 도장 위치/크기 (ctr 76.2%,84.6% 지름~26%폭)
    logo: {
      position: { top: '84.6%', left: '76.2%', transform: 'translate(-50%, -50%)' },
      size: { width: 'clamp(110px, 24vw, 190px)', height: 'auto', maxHeight: '19%' },
    },
    // 강사 1 서명 — 좌측 서명선1(cx 28.3%, y 79.9%) 바로 위
    signature: {
      position: { bottom: '21%', left: '28.3%', transform: 'translateX(-50%)' },
      size: { width: 'clamp(90px, 17vw, 150px)', height: 'auto', maxHeight: '48px' },
    },
    // 강사 2 서명 — 좌측 서명선2(cx 28.3%, y 93.3%) 바로 위
    signature2: {
      position: { bottom: '7.5%', left: '28.3%', transform: 'translateX(-50%)' },
      size: { width: 'clamp(90px, 17vw, 150px)', height: 'auto', maxHeight: '48px' },
    },
    // 수강생 이름 — baked 박스 안(ctr 44.4%), cx 50%. Montserrat thin
    studentName: {
      position: { top: '44.4%', left: '49.9%', transform: 'translate(-50%, -50%)' },
      size: { width: '62%' },
      text: {
        fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif",
        fontWeight: 100,
        fontSize: 'clamp(21px, 3vw, 40px)',
        color: '#1f2937',
        letterSpacing: '0.01em',
        textAlign: 'center',
      },
    },
    // 코스명 — 타이틀 아래(ctr 25.6%), cx 50%. Noto Serif KR extralight, 네이비
    courseName: {
      position: { top: '25.6%', left: '49.8%', transform: 'translate(-50%, -50%)' },
      size: { width: '84%' },
      text: {
        fontFamily: "'Noto Serif KR', serif",
        fontWeight: 200,
        fontSize: 'clamp(16px, 2.2vw, 30px)',
        color: '#4d58a7',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
    // 강사 1 이름 또는 직책 — 서명선1 아래(ctr 82.5%). Montserrat regular
    signerInfo: {
      container: { position: { left: '28.3%', bottom: '16.8%', transform: 'translateX(-50%)' } },
      name: {
        fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(11px, 1.3vw, 16px)',
        color: '#374151',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
      },
      title: {
        fontFamily: "'Montserrat', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(9px, 1vw, 13px)',
        color: '#6b7280',
      },
    },
    // 강사 2 이름 또는 직책 — 서명선2 아래(ctr 95.8%). Montserrat regular
    signerInfo2: {
      container: { position: { left: '28.3%', bottom: '3.3%', transform: 'translateX(-50%)' } },
      name: {
        fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(11px, 1.3vw, 16px)',
        color: '#374151',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
      },
    },
    // QR + 도장 — 우상단 (template 3 스타일). 세로형이라 컴팩트, 번호 당김 완만
    qrCode: {
      container: { position: { top: '4%', right: '4%' } },
      layout: 'horizontal',
      // 실제 도장(=sealHeight*0.6) ≈ qrSize 로 QR과 도장 높이 매칭
      qrSize: 'clamp(34px, 3.6vw, 46px)',
      sealHeight: 'clamp(57px, 6vw, 77px)',
      serialPullRatio: 0.05,
      text: {
        fontFamily: "'Noto Sans', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(7px, 0.8vw, 10px)',
        color: '#6b7280',
        textAlign: 'center',
      },
    },
    // 날짜 — 중앙(ctr 67.5%), "MAY 12, 2026". Noto Sans regular
    completionDate: {
      position: { top: '67.5%', left: '49.9%', transform: 'translate(-50%, -50%)' },
      text: {
        fontFamily: "'Noto Sans', 'Noto Sans KR', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(12px, 1.35vw, 18px)',
        color: '#1f2937',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
  },
  // 템플릿 15: 세로형(A4 세로) CERTIFICATE OF QUALIFICATION — 강사 1명, 좌측 네이비 사이드바, 중앙 워터마크
  // (template_15 1385x1958 세로. 콘텐츠 좌정렬 x12.5%. 하단 좌선 ctr27.4%·우선 ctr78.2%, y84.3%)
  15: {
    backgroundImage: '/certificate-templates/template_15.png',
    hasBorder: false,
    portrait: true,
    hideSignerTitle: true,
    hideCourseHours: true,
    dateMonthName: true,
    logoOpacity: 0.15,
    // 기업 로고 — 중앙 큰 워터마크 (exam STAMP FIT 위치/크기: ctr 54.6%,53.5% 지름~48%폭)
    logo: {
      position: { top: '53.5%', left: '54.6%', transform: 'translate(-50%, -50%)' },
      size: { width: 'clamp(200px, 42vw, 360px)', height: 'auto', maxHeight: '34%' },
    },
    // 강사 서명 — 좌측 서명선(cx 27.4%, y 84.3%) 위. 날짜값과 바닥 정렬(bottom 17%)
    signature: {
      position: { bottom: '17%', left: '27.4%', transform: 'translateX(-50%)' },
      size: { width: 'clamp(90px, 17vw, 150px)', height: 'auto', maxHeight: '48px' },
    },
    // 수강생 이름 — 좌정렬(x12.5%), ctrY 49.2%. Noto Serif KR extralight, 검정
    studentName: {
      position: { top: '49.2%', left: '12.5%', transform: 'translateY(-50%)' },
      size: { width: '80%' },
      text: {
        fontFamily: "'Noto Serif KR', serif",
        fontWeight: 200,
        fontSize: 'clamp(28px, 4vw, 50px)',
        color: '#1a1a1a',
        letterSpacing: '0.01em',
        textAlign: 'left',
      },
    },
    // 코스명 — 타이틀 아래 부제목, 좌정렬(x12.6%) ctrY 21%. Montserrat thin, 넓은 자간, 블루그레이
    courseName: {
      position: { top: '21%', left: '12.6%', transform: 'translateY(-50%)' },
      size: { width: '84%' },
      text: {
        fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif",
        fontWeight: 100,
        fontSize: 'clamp(12px, 1.7vw, 23px)',
        color: '#5f7185',
        letterSpacing: '0.3em',
        textTransform: 'uppercase',
        textAlign: 'left',
      },
    },
    // 강사 이름 또는 직책 — 좌측 서명선 아래(ctrY 86.1%). Noto Sans regular
    signerInfo: {
      container: { position: { left: '27.4%', bottom: '13.2%', transform: 'translateX(-50%)' } },
      name: {
        fontFamily: "'Noto Sans', 'Noto Sans KR', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(12px, 1.4vw, 18px)',
        color: '#1a1a1a',
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
      },
      title: {
        fontFamily: "'Noto Sans', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(9px, 1vw, 13px)',
        color: '#6b7280',
      },
    },
    // QR + 도장 — 우상단 (template 3 스타일). 세로형 컴팩트, 번호 당김 완만
    qrCode: {
      container: { position: { top: '4%', right: '4%' } },
      layout: 'horizontal',
      // 실제 도장(=sealHeight*0.6) ≈ qrSize 로 QR과 도장 높이 매칭
      qrSize: 'clamp(34px, 3.6vw, 46px)',
      sealHeight: 'clamp(57px, 6vw, 77px)',
      serialPullRatio: 0.05,
      text: {
        fontFamily: "'Noto Sans', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(7px, 0.8vw, 10px)',
        color: '#6b7280',
        textAlign: 'center',
      },
    },
    // 날짜값 — 우측 선(cx 78.2%) 위, DATE 위. 서명과 바닥 정렬(bottom 17%). Noto Sans KR thin
    completionDate: {
      position: { bottom: '17%', left: '78.2%', transform: 'translateX(-50%)' },
      text: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 100,
        fontSize: 'clamp(12px, 1.5vw, 19px)',
        color: '#1f2937',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
    // "DATE" 라벨 — 우측 선 아래(ctrY 86.1%). 강사명(INSTRUCTOR)과 동일: Noto Sans regular, 동일 크기·높이
    dateLabel: {
      container: { position: { left: '78.2%', bottom: '13.2%', transform: 'translateX(-50%)' } },
      text: {
        fontFamily: "'Noto Sans', 'Noto Sans KR', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(12px, 1.4vw, 18px)',
        color: '#1a1a1a',
        letterSpacing: '0.1em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
  },
  // 템플릿 16: 세로형 CERTIFICATE OF PARTICIPATION — 강사 1명, 골드 오너먼트 테두리·크라운, 중앙 워터마크
  // (template_16 1385x1958. 콘텐츠 cX50%. 하단선 y77.3%, 좌 cX23.7%(서명)·우 cX76.2%(날짜))
  16: {
    backgroundImage: '/certificate-templates/template_16.png',
    hasBorder: false,
    portrait: true,
    hideSignerTitle: true,
    hideCourseHours: true,
    dateMonthName: true,
    logoOpacity: 0.15,
    // 기업 로고 — 중앙 큰 워터마크 (exam STAMP FIT: ctr 50%,47% 지름~47%)
    logo: {
      position: { top: '47%', left: '50%', transform: 'translate(-50%, -50%)' },
      size: { width: 'clamp(200px, 42vw, 340px)', height: 'auto', maxHeight: '34%' },
    },
    // 강사 서명 — 좌측 선(cx 23.7%, y77.3%) 위. 날짜값과 바닥 정렬(bottom 23.5%)
    signature: {
      position: { bottom: '23.5%', left: '23.7%', transform: 'translateX(-50%)' },
      size: { width: 'clamp(90px, 17vw, 150px)', height: 'auto', maxHeight: '46px' },
    },
    // 수강생 이름 — 중앙, 위로 이동(ctrY 45%). Charm Regular, 골드브라운, 크게
    studentName: {
      position: { top: '45%', left: '50%', transform: 'translate(-50%, -50%)' },
      size: { width: '90%' },
      text: {
        fontFamily: "'Charm', 'Noto Sans KR', cursive",
        fontWeight: 400,
        fontSize: 'clamp(48px, 9vw, 96px)',
        color: '#a5724c',
        letterSpacing: '0.01em',
        textAlign: 'center',
      },
    },
    // 코스명 — 타이틀 아래 부제목(ctrY 27.9%). Noto Serif KR extralight, 다크그레이
    courseName: {
      position: { top: '27.9%', left: '50%', transform: 'translate(-50%, -50%)' },
      size: { width: '80%' },
      text: {
        fontFamily: "'Noto Serif KR', serif",
        fontWeight: 200,
        fontSize: 'clamp(12px, 1.6vw, 21px)',
        color: '#3a3a3a',
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
    // 강사 이름 또는 직책 — 좌측 선 아래(ctrY 79.3%). Noto Sans KR thin
    signerInfo: {
      container: { position: { left: '23.7%', bottom: '20%', transform: 'translateX(-50%)' } },
      name: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 100,
        fontSize: 'clamp(12px, 1.4vw, 17px)',
        color: '#2e2e2e',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      },
      title: {
        fontFamily: "'Noto Sans', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(9px, 1vw, 13px)',
        color: '#6b7280',
      },
    },
    // QR + 도장 — 우상단, 코너 필리그리(아래 y9.7%) 바로 아래로 내려 안 겹치게. 컴팩트
    qrCode: {
      container: { position: { top: '10%', right: '6%' } },
      layout: 'horizontal',
      // 실제 도장(=sealHeight*0.6) ≈ qrSize 로 QR과 도장 높이 매칭
      qrSize: 'clamp(32px, 3.4vw, 44px)',
      sealHeight: 'clamp(53px, 5.6vw, 73px)',
      serialPullRatio: 0.05,
      text: {
        fontFamily: "'Noto Sans', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(7px, 0.8vw, 10px)',
        color: '#6b7280',
        textAlign: 'center',
      },
    },
    // 날짜값 — 우측 선(cx 76.2%) 위, DATE 위. 서명과 바닥 정렬(bottom 23.5%). Noto Sans KR thin
    completionDate: {
      position: { bottom: '23.5%', left: '76.2%', transform: 'translateX(-50%)' },
      text: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 100,
        fontSize: 'clamp(12px, 1.5vw, 18px)',
        color: '#2e2e2e',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
    // "DATE" 라벨 — 우측 선 아래(ctrY 79.3%). 강사명(INSTRUCTOR)과 동일: Noto Sans KR thin
    dateLabel: {
      container: { position: { left: '76.2%', bottom: '20%', transform: 'translateX(-50%)' } },
      text: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 100,
        fontSize: 'clamp(12px, 1.4vw, 17px)',
        color: '#2e2e2e',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
  },
  // 템플릿 17: 세로형 CERTIFICATE OF COMPLETION — 강사 1명, 실버/그레이 테두리·크라운·리본, 로고 하단중앙
  // (template_17 1385x1958. 콘텐츠 cX50%. 하단선 y75.4%, 좌 cX28.2%(날짜)·우 cX71.7%(서명))
  17: {
    backgroundImage: '/certificate-templates/template_17.png',
    hasBorder: false,
    portrait: true,
    hideSignerTitle: true,
    hideCourseHours: true,
    dateMonthName: true,
    // 기업 로고 — 하단 중앙(exam STAMP FIT: ctr 49.9%,87% 지름~21%). 워터마크 아님(일반)
    logo: {
      position: { top: '87%', left: '49.9%', transform: 'translate(-50%, -50%)' },
      size: { width: 'clamp(110px, 22vw, 150px)', height: 'auto', maxHeight: '15%' },
    },
    // 강사 서명 — 우측 선(cx 71.7%, y75.4%) 위. 날짜값과 바닥 정렬(bottom 25%)
    signature: {
      position: { bottom: '25%', left: '71.7%', transform: 'translateX(-50%)' },
      size: { width: 'clamp(90px, 17vw, 150px)', height: 'auto', maxHeight: '46px' },
    },
    // 수강생 이름 — 중앙(ctrY 49.8%). Noto Serif KR extralight, 그레이
    studentName: {
      position: { top: '49.8%', left: '49.9%', transform: 'translate(-50%, -50%)' },
      size: { width: '84%' },
      text: {
        fontFamily: "'Noto Serif KR', serif",
        fontWeight: 200,
        fontSize: 'clamp(26px, 3.7vw, 46px)',
        color: '#5f5c5e',
        letterSpacing: '0.01em',
        textAlign: 'center',
      },
    },
    // 코스명 — 타이틀 아래(ctrY 35.1%). Noto Serif KR extralight, 그레이
    courseName: {
      position: { top: '35.1%', left: '49.6%', transform: 'translate(-50%, -50%)' },
      size: { width: '80%' },
      text: {
        fontFamily: "'Noto Serif KR', serif",
        fontWeight: 200,
        fontSize: 'clamp(13px, 1.7vw, 23px)',
        color: '#767275',
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
    // 강사 이름 또는 직책 — 우측 선 아래(ctrY 77.2%). Noto Sans KR thin
    signerInfo: {
      container: { position: { left: '71.7%', bottom: '22%', transform: 'translateX(-50%)' } },
      name: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 100,
        fontSize: 'clamp(12px, 1.4vw, 17px)',
        color: '#6e6b6d',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
      },
      title: {
        fontFamily: "'Noto Sans', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(9px, 1vw, 13px)',
        color: '#9ca3af',
      },
    },
    // QR + 도장 — 우상단, 코너 필리그리(아래 y12.1%) 바로 아래로 내려 안 겹치게. 컴팩트
    qrCode: {
      container: { position: { top: '13%', right: '6%' } },
      layout: 'horizontal',
      // 실제 도장(=sealHeight*0.6) ≈ qrSize 로 QR과 도장 높이 매칭
      qrSize: 'clamp(34px, 3.6vw, 46px)',
      sealHeight: 'clamp(57px, 6vw, 77px)',
      serialPullRatio: 0.05,
      text: {
        fontFamily: "'Noto Sans', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(7px, 0.8vw, 10px)',
        color: '#9ca3af',
        textAlign: 'center',
      },
    },
    // 날짜값 — 좌측 선(cx 28.2%) 위, DATE 위. 서명과 바닥 정렬(bottom 25%). Noto Sans KR thin
    completionDate: {
      position: { bottom: '25%', left: '28.2%', transform: 'translateX(-50%)' },
      text: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 100,
        fontSize: 'clamp(12px, 1.5vw, 18px)',
        color: '#6e6b6d',
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
    // "DATE" 라벨 — 좌측 선 아래(ctrY 77.2%). 강사명(INSTRUCTOR)과 동일: Noto Sans KR thin
    dateLabel: {
      container: { position: { left: '28.2%', bottom: '22%', transform: 'translateX(-50%)' } },
      text: {
        fontFamily: "'Noto Sans KR', sans-serif",
        fontWeight: 100,
        fontSize: 'clamp(12px, 1.4vw, 17px)',
        color: '#6e6b6d',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        textAlign: 'center',
      },
    },
  },
  // 템플릿 18: 세로형 CERTIFICATE OF QUALIFICATION(블루 타이틀) — 강사1명, 중앙 워터마크, 서명좌/날짜우
  // (1385x1958. 밑줄 y52%, 좌선 cx21.8%·우선 cx78.3%, 선 y87.5%)
  18: {
    backgroundImage: '/certificate-templates/template_18.png',
    hasBorder: false, portrait: true, hideSignerTitle: true, hideCourseHours: true, dateMonthName: true, logoOpacity: 0.15,
    logo: { position: { top: '52.5%', left: '50%', transform: 'translate(-50%, -50%)' }, size: { width: 'clamp(220px, 45vw, 380px)', height: 'auto', maxHeight: '42%' } },
    signature: { position: { bottom: '14%', left: '21.8%', transform: 'translateX(-50%)' }, size: { width: 'clamp(90px, 17vw, 150px)', height: 'auto', maxHeight: '46px' } },
    studentName: { position: { top: '49%', left: '50%', transform: 'translate(-50%, -50%)' }, size: { width: '80%' },
      text: { fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", fontWeight: 100, fontSize: 'clamp(30px, 4.3vw, 54px)', color: '#2a2a2a', letterSpacing: '0.01em', textAlign: 'center' } },
    courseName: { position: { top: '22%', left: '50%', transform: 'translate(-50%, -50%)' }, size: { width: '84%' },
      text: { fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 100, fontSize: 'clamp(13px, 1.7vw, 23px)', color: '#7a8290', letterSpacing: '0.25em', textTransform: 'uppercase', textAlign: 'center' } },
    signerInfo: { container: { position: { left: '21.8%', bottom: '9.5%', transform: 'translateX(-50%)' } },
      name: { fontFamily: "'Noto Serif KR', serif", fontWeight: 200, fontSize: 'clamp(12px, 1.4vw, 17px)', color: '#1a1a1a', letterSpacing: '0.06em', textTransform: 'uppercase' },
      title: { fontFamily: "'Noto Serif KR', serif", fontWeight: 200, fontSize: 'clamp(9px, 1vw, 13px)', color: '#6b7280' } },
    qrCode: { container: { position: { top: '4%', right: '4%' } }, layout: 'horizontal', qrSize: 'clamp(34px, 3.6vw, 46px)', sealHeight: 'clamp(57px, 6vw, 77px)', serialPullRatio: 0.05,
      text: { fontFamily: "'Noto Sans', sans-serif", fontWeight: 400, fontSize: 'clamp(7px, 0.8vw, 10px)', color: '#6b7280', textAlign: 'center' } },
    completionDate: { position: { bottom: '14%', left: '78.3%', transform: 'translateX(-50%)' },
      text: { fontFamily: "'Noto Serif KR', serif", fontWeight: 200, fontSize: 'clamp(13px, 1.55vw, 19px)', color: '#1a1a1a', letterSpacing: '0.03em', textTransform: 'uppercase', textAlign: 'center' } },
    dateLabel: { container: { position: { left: '78.3%', bottom: '9.5%', transform: 'translateX(-50%)' } },
      text: { fontFamily: "'Noto Serif KR', serif", fontWeight: 200, fontSize: 'clamp(12px, 1.4vw, 17px)', color: '#1a1a1a', letterSpacing: '0.06em', textTransform: 'uppercase', textAlign: 'center' } },
  },
  // 템플릿 19: 세로형 CERTIFICATE OF QUALIFICATION(퍼플·메달) — 강사1명, 로고 좌하단, 서명/날짜 우측 2단
  // (밑줄 y50.4%, 우측 선1 y77.7%·선2 y90.5%, cx72%)
  19: {
    backgroundImage: '/certificate-templates/template_19.png',
    hasBorder: false, portrait: true, hideSignerTitle: true, hideCourseHours: true, dateMonthName: true,
    logo: { position: { top: '83.1%', left: '31.1%', transform: 'translate(-50%, -50%)' }, size: { width: 'clamp(120px, 24vw, 175px)', height: 'auto', maxHeight: '20%' } },
    signature: { position: { bottom: '23%', left: '72.3%', transform: 'translateX(-50%)' }, size: { width: 'clamp(90px, 16vw, 145px)', height: 'auto', maxHeight: '44px' } },
    studentName: { position: { top: '47%', left: '50%', transform: 'translate(-50%, -50%)' }, size: { width: '82%' },
      text: { fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", fontWeight: 100, fontSize: 'clamp(30px, 4.4vw, 56px)', color: '#333333', letterSpacing: '0.01em', textAlign: 'center' } },
    courseName: { position: { top: '25.7%', left: '50%', transform: 'translate(-50%, -50%)' }, size: { width: '82%' },
      text: { fontFamily: "'Noto Serif KR', serif", fontWeight: 200, fontSize: 'clamp(14px, 1.9vw, 25px)', color: '#333333', letterSpacing: '0.1em', textTransform: 'uppercase', textAlign: 'center' } },
    signerInfo: { container: { position: { left: '72.2%', bottom: '19%', transform: 'translateX(-50%)' } },
      name: { fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 100, fontSize: 'clamp(12px, 1.4vw, 17px)', color: '#333333', letterSpacing: '0.08em', textTransform: 'uppercase' },
      title: { fontFamily: "'Noto Sans', sans-serif", fontWeight: 400, fontSize: 'clamp(9px, 1vw, 13px)', color: '#6b7280' } },
    qrCode: { container: { position: { top: '4%', right: '4%' } }, layout: 'horizontal', qrSize: 'clamp(34px, 3.6vw, 46px)', sealHeight: 'clamp(57px, 6vw, 77px)', serialPullRatio: 0.05,
      text: { fontFamily: "'Noto Sans', sans-serif", fontWeight: 400, fontSize: 'clamp(7px, 0.8vw, 10px)', color: '#6b7280', textAlign: 'center' } },
    completionDate: { position: { bottom: '10.5%', left: '72.1%', transform: 'translateX(-50%)' },
      text: { fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 100, fontSize: 'clamp(13px, 1.5vw, 18px)', color: '#333333', letterSpacing: '0.05em', textTransform: 'uppercase', textAlign: 'center' } },
    dateLabel: { container: { position: { left: '72.2%', bottom: '6.5%', transform: 'translateX(-50%)' } },
      text: { fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 100, fontSize: 'clamp(12px, 1.4vw, 17px)', color: '#333333', letterSpacing: '0.08em', textTransform: 'uppercase', textAlign: 'center' } },
  },
  // 템플릿 20: 세로형 CERTIFICATE OF PARTICIPATION(다크 네이비·골드) — 강사1명, 로고 하단중앙, 서명좌/날짜우
  // (밑줄 y53%, 하단선 y78%, 좌 cx23.8%·우 cx75.9%)
  20: {
    backgroundImage: '/certificate-templates/template_20.png',
    hasBorder: false, portrait: true, hideSignerTitle: true, hideCourseHours: true, dateMonthName: true,
    logo: { position: { top: '89.2%', left: '50%', transform: 'translate(-50%, -50%)' }, size: { width: 'clamp(115px, 23vw, 160px)', height: 'auto', maxHeight: '16%' } },
    signature: { position: { bottom: '22%', left: '23.8%', transform: 'translateX(-50%)' }, size: { width: 'clamp(90px, 16vw, 145px)', height: 'auto', maxHeight: '46px' } },
    studentName: { position: { top: '46%', left: '50%', transform: 'translate(-50%, -50%)' }, size: { width: '82%' },
      text: { fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", fontWeight: 100, fontSize: 'clamp(32px, 4.9vw, 64px)', color: '#ffffff', letterSpacing: '0.01em', textAlign: 'center' } },
    courseName: { position: { top: '26%', left: '50%', transform: 'translate(-50%, -50%)' }, size: { width: '86%' },
      text: { fontFamily: "'Noto Serif KR', serif", fontWeight: 200, fontSize: 'clamp(18px, 2.5vw, 34px)', color: '#f5f5f5', letterSpacing: '0.05em', textTransform: 'uppercase', textAlign: 'center' } },
    signerInfo: { container: { position: { left: '23.8%', bottom: '19%', transform: 'translateX(-50%)' } },
      name: { fontFamily: "'Poppins', 'Noto Sans KR', sans-serif", fontWeight: 400, fontSize: 'clamp(11px, 1.3vw, 16px)', color: '#ffffff', letterSpacing: '0.12em', textTransform: 'uppercase' },
      title: { fontFamily: "'Poppins', sans-serif", fontWeight: 400, fontSize: 'clamp(9px, 1vw, 13px)', color: '#cbd5e1' } },
    qrCode: { container: { position: { top: '2%', right: '4%' } }, layout: 'horizontal', qrSize: 'clamp(34px, 3.6vw, 46px)', sealHeight: 'clamp(57px, 6vw, 77px)', serialPullRatio: 0.05,
      text: { fontFamily: "'Noto Sans', sans-serif", fontWeight: 400, fontSize: 'clamp(7px, 0.8vw, 10px)', color: '#cbd5e1', textAlign: 'center' } },
    completionDate: { position: { bottom: '22%', left: '75.9%', transform: 'translateX(-50%)' },
      text: { fontFamily: "'Poppins', 'Noto Sans KR', sans-serif", fontWeight: 500, fontSize: 'clamp(13px, 1.5vw, 19px)', color: '#d4a843', letterSpacing: '0.06em', textTransform: 'uppercase', textAlign: 'center' } },
    dateLabel: { container: { position: { left: '75.9%', bottom: '19%', transform: 'translateX(-50%)' } },
      text: { fontFamily: "'Poppins', 'Noto Sans KR', sans-serif", fontWeight: 400, fontSize: 'clamp(11px, 1.3vw, 16px)', color: '#ffffff', letterSpacing: '0.12em', textTransform: 'uppercase', textAlign: 'center' } },
  },
  // 템플릿 21: 세로형 CERTIFICATE(웨이브 좌측·우측정렬) — 강사1명, 중앙 워터마크, 라벨상단/값하단
  // (콘텐츠 cx~60%, 밑줄 y52.8%, 하단 라벨행 y83%·값행 y86.3%)
  21: {
    backgroundImage: '/certificate-templates/template_21.png',
    hasBorder: false, portrait: true, hideSignerTitle: true, hideCourseHours: true, dateMonthName: true, logoOpacity: 0.15,
    logo: { position: { top: '46.5%', left: '55%', transform: 'translate(-50%, -50%)' }, size: { width: 'clamp(220px, 44vw, 380px)', height: 'auto', maxHeight: '40%' } },
    signature: { position: { bottom: '12.5%', left: '48%', transform: 'translateX(-50%)' }, size: { width: 'clamp(80px, 15vw, 135px)', height: 'auto', maxHeight: '42px' } },
    studentName: { position: { top: '47.5%', left: '61%', transform: 'translate(-50%, -50%)' }, size: { width: '70%' },
      text: { fontFamily: "'Caveat', 'Noto Sans KR', cursive", fontWeight: 400, fontSize: 'clamp(40px, 7vw, 78px)', color: '#1a2340', letterSpacing: '0.01em', textAlign: 'center' } },
    courseName: { position: { top: '35.5%', left: '60%', transform: 'translate(-50%, -50%)' }, size: { width: '74%' },
      text: { fontFamily: "'Noto Serif KR', serif", fontWeight: 700, fontSize: 'clamp(16px, 2.1vw, 28px)', color: '#1a2340', letterSpacing: '0.03em', textTransform: 'uppercase', textAlign: 'center' } },
    signerInfo: { container: { position: { left: '47%', bottom: '16.5%', transform: 'translateX(-50%)' } },
      name: { fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 100, fontSize: 'clamp(12px, 1.4vw, 17px)', color: '#3a3a3a', letterSpacing: '0.08em', textTransform: 'uppercase' },
      title: { fontFamily: "'Noto Sans', sans-serif", fontWeight: 400, fontSize: 'clamp(9px, 1vw, 13px)', color: '#6b7280' } },
    qrCode: { container: { position: { top: '4%', right: '4%' } }, layout: 'horizontal', qrSize: 'clamp(34px, 3.6vw, 46px)', sealHeight: 'clamp(57px, 6vw, 77px)', serialPullRatio: 0.05,
      text: { fontFamily: "'Noto Sans', sans-serif", fontWeight: 400, fontSize: 'clamp(7px, 0.8vw, 10px)', color: '#6b7280', textAlign: 'center' } },
    completionDate: { position: { bottom: '13.5%', left: '26%', transform: 'translateX(-50%)' },
      text: { fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 100, fontSize: 'clamp(13px, 1.5vw, 18px)', color: '#3a3a3a', letterSpacing: '0.05em', textTransform: 'uppercase', textAlign: 'center' } },
    dateLabel: { container: { position: { left: '26%', bottom: '16.5%', transform: 'translateX(-50%)' } },
      text: { fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 100, fontSize: 'clamp(12px, 1.4vw, 17px)', color: '#3a3a3a', letterSpacing: '0.08em', textTransform: 'uppercase', textAlign: 'center' } },
  },
  // 템플릿 22: 세로형 CERTIFICATE OF QUALIFICATION(코너 웨이브·메달) — 강사1명, 로고 우하단, 날짜/서명 좌측 2단
  // (밑줄 y56%, 좌측 cx30.7%, 날짜값 y74.8%·DATE y78%·서명 y84.2%·INSTRUCTOR y88.6%)
  22: {
    backgroundImage: '/certificate-templates/template_22.png',
    hasBorder: false, portrait: true, hideSignerTitle: true, hideCourseHours: true, dateMonthName: true,
    logo: { position: { top: '82.2%', left: '68.2%', transform: 'translate(-50%, -50%)' }, size: { width: 'clamp(130px, 26vw, 185px)', height: 'auto', maxHeight: '20%' } },
    signature: { position: { bottom: '14.3%', left: '30.7%', transform: 'translateX(-50%)' }, size: { width: 'clamp(90px, 16vw, 145px)', height: 'auto', maxHeight: '44px' } },
    studentName: { position: { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }, size: { width: '88%' },
      text: { fontFamily: "'Charm', 'Noto Sans KR', cursive", fontWeight: 400, fontSize: 'clamp(44px, 8vw, 86px)', color: '#22315f', letterSpacing: '0.01em', textAlign: 'center' } },
    courseName: { position: { top: '38.8%', left: '50%', transform: 'translate(-50%, -50%)' }, size: { width: '80%' },
      text: { fontFamily: "'Noto Serif KR', serif", fontWeight: 200, fontSize: 'clamp(15px, 2vw, 27px)', color: '#22315f', letterSpacing: '0.08em', textTransform: 'uppercase', textAlign: 'center' } },
    signerInfo: { container: { position: { left: '30.7%', bottom: '10.6%', transform: 'translateX(-50%)' } },
      name: { fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 100, fontSize: 'clamp(12px, 1.4vw, 17px)', color: '#22315f', letterSpacing: '0.08em', textTransform: 'uppercase' },
      title: { fontFamily: "'Noto Sans', sans-serif", fontWeight: 400, fontSize: 'clamp(9px, 1vw, 13px)', color: '#6b7280' } },
    qrCode: { container: { position: { top: '4%', right: '4%' } }, layout: 'horizontal', qrSize: 'clamp(34px, 3.6vw, 46px)', sealHeight: 'clamp(57px, 6vw, 77px)', serialPullRatio: 0.05,
      text: { fontFamily: "'Noto Sans', sans-serif", fontWeight: 400, fontSize: 'clamp(7px, 0.8vw, 10px)', color: '#6b7280', textAlign: 'center' } },
    completionDate: { position: { bottom: '24.4%', left: '30.7%', transform: 'translateX(-50%)' },
      text: { fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 100, fontSize: 'clamp(13px, 1.5vw, 18px)', color: '#22315f', letterSpacing: '0.05em', textTransform: 'uppercase', textAlign: 'center' } },
    dateLabel: { container: { position: { left: '30.7%', bottom: '21.2%', transform: 'translateX(-50%)' } },
      text: { fontFamily: "'Noto Sans KR', sans-serif", fontWeight: 100, fontSize: 'clamp(12px, 1.4vw, 17px)', color: '#22315f', letterSpacing: '0.08em', textTransform: 'uppercase', textAlign: 'center' } },
  },
};
